/**
 * useShellAttackAftermath - what an attack roll and a reaction spend.
 *
 * - `onAttackRolled({ isMelee, specialRules })`: the dice left the cup. The
 *   Cycling capacitor is spent; aim (and, in melee, a won feint) is used up;
 *   Charge and All-Out's bonus is spent by the swing (v8.310.7) - through the
 *   RAW setter, on purpose, so the price (`Ao`) is not refunded; and
 *   Suppressing Fire pins its zone (v8.307.0).
 * - `onReactionTaken(tabId)`: RAW, the reactor's aim is lost.
 *
 * SUPPRESSION runs here rather than in an effect or after the damage: on
 * the SHOOTER's client only, so the table sees each line once; inside
 * handleAttack's own history snapshot, so one Ctrl+Z takes back the burst
 * and the pinning together; and its zone comes from `suppressionZone`, the
 * same function that chose the victims.
 *
 * DICE CONTRACT: attack d100, then one WP test per occupant in zone order
 * (an immune one rolls nothing), then the burst's scatter d10s, then damage.
 * Extracted from DeathWatchSimulator; every log line is unchanged.
 */
import { useCallback } from 'react';
import { condType } from '../engine/conditions';
import { consumeAfterAttack, dropOnReaction } from '../engine/preparedStates';
import { projectDefender } from '../engine/roster/projection';
import { reactionExposure } from '../engine/exposures';
import { rollPinningTest, SUPPRESSION_PIN_MOD } from '../engine/pinning';
import { clearRawAim, expireConditions, infoLine } from './shellCombatant';

/** Pinned, once: `Pn` is added only when it is not already there. */
function pinnedWrite(st) {
  return {
    ...st,
    conditions: (st.conditions ?? []).some((c) => condType(c) === 'Pn')
      ? (st.conditions ?? [])
      : [...(st.conditions ?? []), 'Pn'],
  };
}

/** The shooter's `pin` exposure (v8.368.5), against this occupant. */
function pinModAgainst(actorProjection, projected) {
  return reactionExposure(
    {
      conditions: actorProjection?.conditions ?? [],
      exposures: actorProjection?.exposures ?? [],
      teamTag: actorProjection?.teamTag ?? null,
      sideTag: actorProjection?.sideTag ?? null,
    },
    { teamTag: projected.teamTag ?? null, sideTag: projected.sideTag ?? null, isMelee: false },
  ).pinMod;
}

/** Everyone in the zone tests Willpower at Hard (-20) or goes to ground, hit or not. */
function pinTheZone(ids, { roster, actorProjection, updateCombatantState, addRollNarrative }) {
  for (const id of ids) {
    const victim = roster.find((c) => c.id === id);
    if (!victim) continue;
    const projected = projectDefender(victim);
    const out = rollPinningTest(projected, { mod: SUPPRESSION_PIN_MOD + pinModAgainst(actorProjection, projected) });
    if (out.pinned) updateCombatantState(id, pinnedWrite);
    addRollNarrative(infoLine(`📌 Suppression - ${victim.name || id}: ${out.summary}`));
  }
}

/** Aim spent: the actor's conditions, or the scratch rack's raw toggles. */
function spendAim(isMelee, deps) {
  if (!deps.actor) {
    clearRawAim(deps.rawAttackToggles, deps.setRawAttackToggles);
    return;
  }
  const spent = expireConditions(
    deps.actor,
    (conds) => consumeAfterAttack(conds, { isMelee }),
    deps.updateCombatantState,
  );
  if (spent) deps.addRollNarrative(infoLine(`⏱ ${spent} spent by the attack.`));
}

/**
 * THE BONUS IS SPENT BY THE SWING; THE PRICE IS NOT (v8.310.7). Silently
 * (v8.310.15): the pill going out says it. Guarded is not spent - its +10
 * is a defensive promise that must outlive the swing.
 */
function spendCharge(isMelee, { rawAttackToggles, setRawAttackToggles }) {
  if (isMelee && (rawAttackToggles.charge || rawAttackToggles.allOutAttack)) {
    setRawAttackToggles((t) => ({ ...t, charge: false, allOutAttack: false }));
  }
}

/** The whole aftermath of one attack roll, in the order the dice contract names. */
function afterAttackRoll({ isMelee, specialRules }, deps) {
  deps.startCooldown(specialRules);
  spendAim(isMelee, deps);
  spendCharge(isMelee, deps);
  if (deps.attackMode !== 'suppressing') return;
  deps.setAttackMode(null);
  pinTheZone(deps.suppressionZone(deps.effectiveSlot, deps.activeTab), deps);
}

/** The attack-roll callback, over the inline version's dependency list (over length: bindings only). */
function useAttackRolled(args) {
  const { actor, actorProjection, roster, activeTab, effectiveSlot, rawAttackToggles, attackMode } = args;
  const { startCooldown, suppressionZone, setRawAttackToggles, setAttackMode, updateCombatantState } = args;
  const { addRollNarrative } = args;
  return useCallback(
    (roll) =>
      afterAttackRoll(roll, {
        actor,
        actorProjection,
        roster,
        activeTab,
        effectiveSlot,
        rawAttackToggles,
        attackMode,
        startCooldown,
        suppressionZone,
        setRawAttackToggles,
        setAttackMode,
        updateCombatantState,
        addRollNarrative,
      }),
    [
      actorProjection,
      actor,
      updateCombatantState,
      rawAttackToggles,
      addRollNarrative,
      effectiveSlot,
      startCooldown,
      attackMode,
      suppressionZone,
      activeTab,
      roster,
      setRawAttackToggles,
      setAttackMode,
    ],
  );
}

/**
 * @param {object} args - actor, actorProjection, roster, activeTab,
 *   effectiveSlot, rawAttackToggles, attackMode, and the writers:
 *   startCooldown, suppressionZone, setRawAttackToggles, setAttackMode,
 *   updateCombatantState, addRollNarrative, addNarrative.
 * @returns {{ onAttackRolled: Function, onReactionTaken: Function }}
 *
 * `onAttackRolled` depends on everything it reads; it once omitted
 * `actorProjection` (read by the pin exposure) and served the previous value
 * after a change (v8.377.3).
 */
export default function useShellAttackAftermath(args) {
  const { roster, updateCombatantState, addNarrative } = args;
  const onReactionTaken = useCallback(
    (tabId) => {
      const reactor = roster.find((c) => c.id === tabId);
      const lost = reactor ? expireConditions(reactor, dropOnReaction, updateCombatantState) : null;
      if (lost) addNarrative(tabId, infoLine(`⏱ ${lost} lost - a Reaction was taken.`));
    },
    [roster, updateCombatantState, addNarrative],
  );
  return { onAttackRolled: useAttackRolled(args), onReactionTaken };
}
